import { Alert, Anchor, Badge, Button, Card, Group, Modal, Progress, Radio, Stack, Text, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconCloudCheck, IconCloudOff, IconInfoCircle, IconLoader2, IconSend } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError, api } from '../../lib/api';
import type { AttemptResult, QuizAttempt } from '../../lib/types';
import { QueryState } from '../../ui/QueryState';

type SaveState = 'saved' | 'saving' | 'offline';

export function QuizPage() {
  const assignmentId = Number(useParams().assignmentId);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [save, setSave] = useState<Record<string, SaveState>>({});
  const [confirm, { open, close }] = useDisclosure(false);
  const [submitted, setSubmitted] = useState(false);
  const pending = useRef(new Map<string, number>());

  // Старт идемпотентен: незавершённая попытка возвращается та же, с теми же вопросами и сохранёнными ответами
  const attempt = useQuery({
    queryKey: ['attempt-start', assignmentId],
    queryFn: () => api.post<QuizAttempt>(`/me/courses/${assignmentId}/quiz-attempts`),
    retry: false,
    staleTime: Infinity,
    enabled: !submitted,
  });
  useEffect(() => {
    if (attempt.data) {
      setAnswers(attempt.data.saved);
      setSave(Object.fromEntries(Object.keys(attempt.data.saved).map((k) => [k, 'saved' as SaveState])));
    }
  }, [attempt.data]);

  // Каждый ответ сохраняется на сервере сразу; при обрыве связи повторяется, попытка не тратится
  const push = useCallback(async (qid: string, answerId: number) => {
    if (!attempt.data) return;
    setSave((s) => ({ ...s, [qid]: 'saving' }));
    try {
      await api.put(`/me/quiz-attempts/${attempt.data.attemptId}/responses/${qid}`, { answerId });
      pending.current.delete(qid);
      setSave((s) => ({ ...s, [qid]: 'saved' }));
    } catch (e) {
      pending.current.set(qid, answerId);
      setSave((s) => ({ ...s, [qid]: 'offline' }));
      if ((e as ApiError).status !== 0) notifications.show({ color: 'red', message: (e as Error).message });
    }
  }, [attempt.data]);

  useEffect(() => {
    const retry = () => pending.current.forEach((aid, qid) => void push(qid, aid));
    window.addEventListener('online', retry);
    const t = window.setInterval(retry, 10_000);
    return () => {
      window.removeEventListener('online', retry);
      window.clearInterval(t);
    };
  }, [push]);

  const submit = useMutation({
    mutationFn: () => api.post<AttemptResult>(`/me/quiz-attempts/${attempt.data!.attemptId}/submit`, { answers }),
    onSuccess: async (r) => {
      setSubmitted(true);
      qc.removeQueries({ queryKey: ['attempt-start', assignmentId] });
      await qc.invalidateQueries({ queryKey: ['course', assignmentId] });
      await qc.invalidateQueries({ queryKey: ['my-courses'] });
      navigate(`/results/${r.attemptId}`, { replace: true });
    },
    onError: (e: Error) => {
      close();
      notifications.show({ color: 'red', message: (e as ApiError).status === 0 ? 'Нет связи. Ответы сохранены, отправьте тест, когда связь восстановится - попытка не потрачена.' : e.message });
    },
  });

  const offline = Object.values(save).filter((s) => s === 'offline').length;

  return (
    <Stack maw={820} mx="auto">
      <Anchor component={Link} to={`/learn/${assignmentId}`} size="sm"><IconArrowLeft size={14} style={{ verticalAlign: -2 }} /> К курсу</Anchor>
      <QueryState loading={attempt.isLoading || submitted || !attempt.data} error={attempt.error}>
        {() => {
          const a = attempt.data!;
          const answered = a.questions.filter((q) => answers[q.id] != null).length;
          const total = a.questions.length;
          return (
            <>
              <Card withBorder radius="lg" padding="lg" pos="sticky" top={68} style={{ zIndex: 5 }}>
                <Group justify="space-between" mb="xs" wrap="nowrap">
                  <div>
                    <Title order={3}>Итоговый тест · попытка {a.attemptNo}</Title>
                    <Text size="sm" c="dimmed">Сдан, если верно не меньше {a.passCount} из {a.questionCount}</Text>
                  </div>
                  <Stack gap={2} align="flex-end">
                    <Text fw={600} size="sm" style={{ whiteSpace: 'nowrap' }}>{answered} / {total}</Text>
                    {offline > 0
                      ? <Badge color="orange" variant="light" leftSection={<IconCloudOff size={11} />}>нет связи</Badge>
                      : <Badge color="teal" variant="light" leftSection={<IconCloudCheck size={11} />}>сохранено</Badge>}
                  </Stack>
                </Group>
                <Progress value={(answered / total) * 100} radius="xl" size="sm" />
              </Card>
              <Alert variant="light" icon={<IconInfoCircle />} color="gray">
                Ответы сохраняются сразу. Если связь оборвётся, тест продолжится с того же места с теми же вопросами - попытка не тратится. Правильные ответы во время теста не показываются.
              </Alert>
              {a.questions.map((q, i) => (
                <Card key={q.id} withBorder radius="lg" padding="lg">
                  <Group justify="space-between" mb={4}>
                    <Text size="xs" c="dimmed">Вопрос {i + 1} из {total}{q.type === 'TRUE_FALSE' ? ' · верно или неверно' : ''}</Text>
                    {save[q.id] === 'saving' && <IconLoader2 size={14} color="gray" />}
                    {save[q.id] === 'offline' && <Text size="xs" c="orange">сохранится при восстановлении связи</Text>}
                  </Group>
                  <Text fw={600} mb="md">{q.text}</Text>
                  <Radio.Group
                    value={answers[q.id] != null ? String(answers[q.id]) : null}
                    onChange={(v) => {
                      setAnswers((s) => ({ ...s, [q.id]: Number(v) }));
                      void push(String(q.id), Number(v));
                    }}
                  >
                    <Stack gap="xs">
                      {q.answers.map((ans) => (
                        <Radio.Card key={ans.id} value={String(ans.id)} radius="md" p="sm" withBorder>
                          <Group wrap="nowrap" gap="sm"><Radio.Indicator /><Text size="sm">{ans.text}</Text></Group>
                        </Radio.Card>
                      ))}
                    </Stack>
                  </Radio.Group>
                </Card>
              ))}
              <Group justify="flex-end" mb="xl">
                <Button size="md" leftSection={<IconSend size={18} />} disabled={answered < total} onClick={open}>
                  {answered < total ? `Ответьте на все вопросы (${total - answered})` : 'Отправить ответы'}
                </Button>
              </Group>
              <Modal opened={confirm} onClose={close} title="Отправить тест?" centered>
                <Text size="sm" mb="md">После отправки ответы изменить нельзя, попытка будет засчитана.</Text>
                <Group justify="flex-end">
                  <Button variant="default" onClick={close}>Проверить ещё раз</Button>
                  <Button loading={submit.isPending} onClick={() => submit.mutate()}>Отправить</Button>
                </Group>
              </Modal>
            </>
          );
        }}
      </QueryState>
    </Stack>
  );
}
